import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import config from '../config';

// ── Entity metadata ──────────────────────────────────────────────────────────
// key: matches the {entityType} path param the adminSharcIds Lambda expects
// publicPath: base route for the "View" link out to the live page
const ENTITY_META = [
  { key: 'artist', label: 'Artists', secondaryLabel: 'City',  publicPath: '/artists' },
  { key: 'event',  label: 'Events',  secondaryLabel: 'Date',  publicPath: '/events'  },
  { key: 'member', label: 'Members', secondaryLabel: 'City',  publicPath: '/members' },
  { key: 'venue',  label: 'Venues',  secondaryLabel: 'City',  publicPath: '/venues'  },
];

const SHARC_ID_PATTERN = /^[a-z0-9-]+$/;

const S = {
  page: {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '40px 24px',
    color: '#e0e0e0',
    fontFamily: 'sans-serif',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: '#bb86fc',
    cursor: 'pointer',
    fontSize: '13px',
    padding: '0 0 16px 0',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  title: {
    fontSize: '26px',
    fontWeight: 'bold',
    color: '#bb86fc',
    margin: '0 0 4px 0',
  },
  subtitle: {
    fontSize: '13px',
    color: '#777',
    marginBottom: '32px',
  },
  tabBar: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
    marginBottom: '20px',
    borderBottom: '1px solid #2a2a2a',
    paddingBottom: '12px',
  },
  tab: {
    padding: '7px 14px',
    borderRadius: '6px',
    border: '1px solid #333',
    backgroundColor: '#121212',
    color: '#aaa',
    cursor: 'pointer',
    fontSize: '13px',
    transition: 'all 0.15s',
  },
  tabActive: {
    backgroundColor: '#bb86fc',
    borderColor: '#bb86fc',
    color: '#000',
    fontWeight: 'bold',
  },
  searchRow: {
    marginBottom: '20px',
  },
  searchInput: {
    backgroundColor: '#2a2a3e',
    border: '1px solid #444',
    borderRadius: '6px',
    color: '#e0e0e0',
    padding: '10px 14px',
    fontSize: '14px',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  hint: {
    fontSize: '12px',
    color: '#555',
    marginTop: '8px',
    lineHeight: '1.5',
  },
  card: {
    backgroundColor: '#1e1e2e',
    border: '1px solid #2a2a2a',
    borderRadius: '10px',
    padding: '8px',
  },
  emptyState: {
    padding: '32px 16px',
    textAlign: 'center',
    color: '#666',
    fontSize: '13px',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 120px 200px 100px',
    gap: '10px',
    alignItems: 'center',
    padding: '10px 12px',
    borderRadius: '6px',
    borderBottom: '1px solid #262636',
  },
  rowLast: {
    borderBottom: 'none',
  },
  rowEditing: {
    backgroundColor: '#22203a',
  },
  nameCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
  },
  nameText: {
    fontSize: '14px',
    color: '#e0e0e0',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  viewLink: {
    fontSize: '11px',
    color: '#888',
    textDecoration: 'none',
  },
  secondaryText: {
    fontSize: '13px',
    color: '#999',
  },
  sharcValue: {
    fontSize: '13px',
    color: '#bb86fc',
    fontFamily: 'monospace',
  },
  sharcEmpty: {
    fontSize: '13px',
    color: '#555',
    fontStyle: 'italic',
  },
  input: {
    backgroundColor: '#2a2a3e',
    border: '1px solid #444',
    borderRadius: '6px',
    color: '#e0e0e0',
    padding: '7px 10px',
    fontSize: '13px',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
    fontFamily: 'monospace',
  },
  actions: {
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  iconBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px 6px',
    borderRadius: '4px',
    fontSize: '15px',
    transition: 'background 0.15s',
  },
  saveBtn: {
    backgroundColor: '#2a7',
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    padding: '5px 10px',
    fontSize: '12px',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  errorMsg: {
    color: '#e57373',
    fontSize: '13px',
    marginTop: '12px',
  },
  successMsg: {
    color: '#81c784',
    fontSize: '13px',
    marginTop: '12px',
  },
  loadingMsg: {
    color: '#777',
    padding: '16px',
  },
  validationMsg: {
    fontSize: '11px',
    color: '#cf6679',
    marginTop: '4px',
  },
};

async function getAuthHeader() {
  const { tokens } = await fetchAuthSession();
  return { Authorization: tokens.idToken.toString() };
}

function formatSecondary(entityKey, value) {
  if (!value) return '—';
  if (entityKey === 'event') {
    return new Date(value).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC'
    });
  }
  return value;
}

export default function AdminManageSharcIds() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(ENTITY_META[0].key);
  const [query, setQuery] = useState('');
  const [mode, setMode] = useState('existing');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);

  const [editStates, setEditStates] = useState({}); // id -> { value, error }
  const [saving, setSaving] = useState(null);        // id currently saving

  const debounceRef = useRef(null);

  const activeMeta = ENTITY_META.find(m => m.key === activeTab);

  const loadResults = useCallback(async (entityKey, q) => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeader();
      const res = await axios.get(`${config.API_URL}/admin/sharc-ids/${entityKey}`, {
        headers,
        params: q ? { q } : {},
      });
      setResults(res.data.results || []);
      setMode(res.data.mode || (q ? 'search' : 'existing'));
    } catch (e) {
      setError('Failed to load records: ' + (e.response?.data?.error || e.message));
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Reload whenever the tab changes (immediate) or the query changes (debounced)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadResults(activeTab, query.trim());
    }, query ? 350 : 0);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, query]);

  function switchTab(key) {
    setActiveTab(key);
    setQuery('');
    setEditStates({});
  }

  function showFlash(type, msg) {
    setFlash({ type, msg });
    setTimeout(() => setFlash(null), 3000);
  }

  function startEdit(row) {
    setEditStates(prev => ({
      ...prev,
      [row.id]: { value: row.sharc_id || '', error: null },
    }));
  }

  function cancelEdit(id) {
    setEditStates(prev => { const n = { ...prev }; delete n[id]; return n; });
  }

  function updateEditValue(id, value) {
    const normalized = value.toLowerCase();
    let err = null;
    if (normalized && !SHARC_ID_PATTERN.test(normalized)) {
      err = 'Only lowercase letters, numbers, and dashes allowed';
    }
    setEditStates(prev => ({ ...prev, [id]: { value: normalized, error: err } }));
  }

  async function saveRow(row) {
    const state = editStates[row.id];
    if (!state) return;
    if (state.error) return;

    setSaving(row.id);
    try {
      const headers = await getAuthHeader();
      const body = { sharc_id: state.value.trim() || null };
      const res = await axios.put(
        `${config.API_URL}/admin/sharc-ids/${activeTab}/${row.id}`,
        body,
        { headers }
      );
      cancelEdit(row.id);
      showFlash('success', body.sharc_id ? 'SHARC ID saved!' : 'SHARC ID cleared.');

      // Update the row in place so the list doesn't need a full reload
      setResults(prev => {
        const updated = prev.map(r => r.id === row.id ? { ...r, sharc_id: res.data.sharc_id } : r);
        // If we're in "existing" mode and the value was cleared, drop it from view
        if (mode === 'existing' && !res.data.sharc_id) {
          return updated.filter(r => r.id !== row.id);
        }
        return updated;
      });
    } catch (e) {
      showFlash('error', e.response?.data?.error || 'Save failed');
    } finally {
      setSaving(null);
    }
  }

  async function clearRow(row) {
    if (!window.confirm(`Clear the SHARC ID for "${row.name}"? This will mark it Unverified.`)) return;
    setSaving(row.id);
    try {
      const headers = await getAuthHeader();
      await axios.put(
        `${config.API_URL}/admin/sharc-ids/${activeTab}/${row.id}`,
        { sharc_id: null },
        { headers }
      );
      showFlash('success', 'SHARC ID cleared.');
      setResults(prev => {
        const updated = prev.map(r => r.id === row.id ? { ...r, sharc_id: null } : r);
        return mode === 'existing' ? updated.filter(r => r.id !== row.id) : updated;
      });
    } catch (e) {
      showFlash('error', e.response?.data?.error || 'Clear failed');
    } finally {
      setSaving(null);
    }
  }

  return (
    <div style={S.page}>
      <button style={S.backBtn} onClick={() => navigate('/admin')}>
        ← Back to Admin Portal
      </button>
      <h1 style={S.title}>Manage SHARC IDs</h1>
      <p style={S.subtitle}>
        Set or clear the SHARC ID (Verified status) for Artist, Event, Member, and Venue records.
      </p>

      {/* Tab bar */}
      <div style={S.tabBar}>
        {ENTITY_META.map(m => (
          <button
            key={m.key}
            style={activeTab === m.key ? { ...S.tab, ...S.tabActive } : S.tab}
            onClick={() => switchTab(m.key)}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div style={S.searchRow}>
        <input
          style={S.searchInput}
          placeholder={`Search ${activeMeta.label.toLowerCase()} by name…`}
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        <div style={S.hint}>
          {query.trim()
            ? `Showing matches for "${query.trim()}".`
            : `Showing ${activeMeta.label.toLowerCase()} that already have a SHARC ID set. Search above to verify a new record.`}
        </div>
      </div>

      {error && <div style={S.errorMsg}>{error}</div>}

      <div style={S.card}>
        {loading && <div style={S.loadingMsg}>Loading…</div>}

        {!loading && !error && results.length === 0 && (
          <div style={S.emptyState}>
            {mode === 'search'
              ? 'No matches found.'
              : `No ${activeMeta.label.toLowerCase()} are verified yet. Search above to add one.`}
          </div>
        )}

        {!loading && results.map((row, i) => {
          const editing = !!editStates[row.id];
          const es = editStates[row.id] || {};
          const isSaving = saving === row.id;
          const isLast = i === results.length - 1;

          return (
            <div
              key={row.id}
              style={{
                ...S.row,
                ...(isLast ? S.rowLast : {}),
                ...(editing ? S.rowEditing : {}),
              }}
            >
              <div style={S.nameCell}>
                <span style={S.nameText}>{row.name}</span>
                <a
                  href={`${activeMeta.publicPath}/${row.slug || row.id}`}
                  target="_blank"
                  rel="noreferrer"
                  style={S.viewLink}
                >
                  View →
                </a>
              </div>

              <span style={S.secondaryText}>{formatSecondary(activeTab, row.secondary)}</span>

              {editing ? (
                <div>
                  <input
                    style={S.input}
                    value={es.value}
                    placeholder="e.g. andyslania"
                    onChange={e => updateEditValue(row.id, e.target.value)}
                    autoFocus
                  />
                  {es.error && <div style={S.validationMsg}>{es.error}</div>}
                </div>
              ) : (
                row.sharc_id
                  ? <span style={S.sharcValue}>{row.sharc_id}</span>
                  : <span style={S.sharcEmpty}>Unverified</span>
              )}

              <div style={S.actions}>
                {editing ? (
                  <>
                    <button
                      style={{ ...S.saveBtn, opacity: (isSaving || !!es.error) ? 0.6 : 1 }}
                      onClick={() => saveRow(row)}
                      disabled={isSaving || !!es.error}
                    >
                      {isSaving ? '…' : '✓'}
                    </button>
                    <button
                      style={{ ...S.iconBtn, color: '#777' }}
                      onClick={() => cancelEdit(row.id)}
                      title="Cancel"
                    >
                      ✕
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      style={{ ...S.iconBtn, color: '#aaa' }}
                      onClick={() => startEdit(row)}
                      title={row.sharc_id ? 'Edit SHARC ID' : 'Set SHARC ID'}
                      disabled={isSaving}
                    >
                      ✏️
                    </button>
                    {row.sharc_id && (
                      <button
                        style={{ ...S.iconBtn, color: '#e57373' }}
                        onClick={() => clearRow(row)}
                        title="Clear SHARC ID"
                        disabled={isSaving}
                      >
                        🗑️
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {flash && (
        <div style={flash.type === 'success' ? S.successMsg : S.errorMsg}>
          {flash.msg}
        </div>
      )}
    </div>
  );
}
